// DashboardCleanModern.js - Dashboard Variation E: "Clean Modern"
// Redesigned with left nav, compact metrics, prioritized recommendations, and assets table

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import {
    ResponsiveContainer,
    AreaChart,
    Area,
    XAxis,
    YAxis,
    Tooltip as RechartsTooltip
} from 'recharts';
import {
    getCommandCenterSummary,
    formatRelativeTime,
    getSeverityColor
} from '../../services/commandCenterService';
import { getAsset } from '../../services/assetService';
import { useThemeColors } from '../../hooks/useThemeColors';
import { SkeletonDashboard } from '../../components/common';
import './DashboardCleanModern.css';

// Left navigation bar
function LeftNav({ activeSection, onSectionChange }) {
    const navigate = useNavigate();
    const location = useLocation();

    const navItems = [
        { id: 'overview', icon: '📊', label: 'Overview', path: '/dashboard-v5' },
        { id: 'assets', icon: '🌐', label: 'Assets', path: '/assets' },
        { id: 'scans', icon: '🔍', label: 'Scans', path: '/scans' },
        { id: 'settings', icon: '⚙️', label: 'Settings', path: '/settings' }
    ];

    return (
        <nav className="cm-left-nav">
            <div className="nav-logo">
                <span className="logo-icon">🛡️</span>
                <span className="logo-text">DriftAlarm</span>
            </div>
            <div className="nav-items">
                {navItems.map(item => (
                    <button
                        key={item.id}
                        className={`nav-item ${location.pathname === item.path ? 'active' : ''}`}
                        onClick={() => navigate(item.path)}
                        title={item.label}
                    >
                        <span className="nav-icon">{item.icon}</span>
                        <span className="nav-label">{item.label}</span>
                    </button>
                ))}
            </div>
            <div className="nav-footer">
                <Link to="/dashboard-prototypes" className="nav-back">
                    ← Prototypes
                </Link>
            </div>
        </nav>
    );
}

// Compact metric card with denominator
function MetricCard({ label, value, total, subtitle, icon, color, onClick }) {
    return (
        <div className="cm-metric-card" onClick={onClick} style={{ cursor: onClick ? 'pointer' : 'default' }}>
            <div className="metric-header">
                <span className="metric-icon">{icon}</span>
                <span className="metric-label">{label}</span>
            </div>
            <div className="metric-value" style={{ color }}>
                {value}
                {total !== undefined && <span className="metric-total">/{total}</span>}
            </div>
            {subtitle && <div className="metric-subtitle">{subtitle}</div>}
        </div>
    );
}

// Severity breakdown card
function SeverityCard({ vulnCounts }) {
    const severities = [
        { key: 'critical', label: 'Critical', color: 'var(--severity-critical)' },
        { key: 'high', label: 'High', color: 'var(--severity-high)' },
        { key: 'medium', label: 'Medium', color: 'var(--severity-medium)' },
        { key: 'low', label: 'Low', color: 'var(--severity-low)' }
    ];

    return (
        <div className="cm-metric-card cm-severity-card">
            <div className="metric-header">
                <span className="metric-icon">📊</span>
                <span className="metric-label">Severity Breakdown</span>
            </div>
            <div className="severity-grid">
                {severities.map(sev => (
                    <div key={sev.key} className="severity-item">
                        <span className="sev-count" style={{ color: sev.color }}>
                            {vulnCounts?.[sev.key] || 0}
                        </span>
                        <span className="sev-label">{sev.label}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

// Activity card with last/next scan
function ActivityCard({ lastScan, nextScan }) {
    return (
        <div className="cm-metric-card cm-activity-card">
            <div className="metric-header">
                <span className="metric-icon">🕐</span>
                <span className="metric-label">Last Activity</span>
            </div>
            <div className="activity-content">
                <div className="activity-row">
                    <span className="activity-label">Last Scan</span>
                    <span className="activity-value">{formatRelativeTime(lastScan)}</span>
                </div>
                <div className="activity-row">
                    <span className="activity-label">Next Scan</span>
                    <span className="activity-value">{nextScan ? formatRelativeTime(nextScan) : 'Manual'}</span>
                </div>
            </div>
        </div>
    );
}

// Collapsible section header
function CollapsibleHeader({ title, icon, isOpen, onToggle, actions }) {
    return (
        <div className="cm-section-header" onClick={onToggle}>
            <div className="header-left">
                <button className={`collapse-btn ${isOpen ? 'open' : ''}`}>
                    <span className="collapse-icon">▶</span>
                </button>
                {icon && <span className="header-icon">{icon}</span>}
                <h2>{title}</h2>
            </div>
            {actions && <div className="header-actions" onClick={e => e.stopPropagation()}>{actions}</div>}
        </div>
    );
}

// Toggle button for red/blue team view
function TeamToggle({ activeTeam, onChange }) {
    return (
        <div className="team-toggle">
            <button
                className={`toggle-btn ${activeTeam === 'red' ? 'active red' : ''}`}
                onClick={() => onChange('red')}
            >
                🔴 Attack Paths
            </button>
            <button
                className={`toggle-btn ${activeTeam === 'blue' ? 'active blue' : ''}`}
                onClick={() => onChange('blue')}
            >
                🔵 Mitigations
            </button>
        </div>
    );
}

// Prioritized recommendations table row
function RecommendationRow({ rec, index, teamView, onClick }) {
    const content = teamView === 'red' ? rec.attackVector : rec.remediation;

    return (
        <tr className="rec-row" onClick={onClick}>
            <td className="col-rank">
                <span className="rank-badge" style={{ background: getSeverityColor(rec.priority?.toLowerCase()) }}>
                    {index + 1}
                </span>
            </td>
            <td className="col-asset">{rec.assetValue}</td>
            <td className="col-vuln">{rec.vulnerability}</td>
            <td className="col-content">{content}</td>
            <td className="col-effort">
                <span className={`effort-badge effort-${rec.effort?.toLowerCase()}`}>
                    {rec.effort}
                </span>
            </td>
        </tr>
    );
}

// Asset table row
function AssetRow({ asset, onClick, onScan }) {
    const getRiskDotClass = (level) => {
        if (level === 'critical') return 'dot-critical';
        if (level === 'atRisk') return 'dot-atrisk';
        if (level === 'caution') return 'dot-caution';
        return 'dot-healthy';
    };

    return (
        <tr className="asset-row" onClick={onClick}>
            <td className="col-status">
                <span className={`risk-dot ${getRiskDotClass(asset.riskLevel)}`} />
            </td>
            <td className="col-name">
                <span className="asset-icon">{asset.assetType === 'domain' ? '🌐' : '🖥️'}</span>
                {asset.value}
            </td>
            <td className="col-score">{asset.riskScore}</td>
            <td className="col-vulns">
                {asset.vulnCounts?.critical > 0 && (
                    <span className="vuln-badge critical">{asset.vulnCounts.critical}C</span>
                )}
                {asset.vulnCounts?.high > 0 && (
                    <span className="vuln-badge high">{asset.vulnCounts.high}H</span>
                )}
                {asset.vulnCounts?.medium > 0 && (
                    <span className="vuln-badge medium">{asset.vulnCounts.medium}M</span>
                )}
            </td>
            <td className="col-time">{formatRelativeTime(asset.lastScanAt)}</td>
            <td className="col-actions">
                <button className="action-btn primary" onClick={(e) => { e.stopPropagation(); onClick(); }}>View</button>
                <button className="action-btn secondary" onClick={(e) => { e.stopPropagation(); onScan?.(asset); }}>Scan</button>
            </td>
        </tr>
    );
}

function DashboardCleanModern() {
    const navigate = useNavigate();
    const themeColors = useThemeColors();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [data, setData] = useState({
        summary: {},
        assets: [],
        topRisks: [],
        trends: {}
    });

    // Aggregated recommendations from all assets
    const [recommendations, setRecommendations] = useState([]);
    const [recsLoading, setRecsLoading] = useState(true);

    // Section collapse state
    const [sectionsOpen, setSectionsOpen] = useState({
        trend: true,
        recommendations: true,
        assets: true
    });

    // Red/Blue team toggle
    const [teamView, setTeamView] = useState('red');

    // Fetch main data
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const result = await getCommandCenterSummary();
                if (result.success) {
                    setData({
                        summary: result.summary || {},
                        assets: result.assets || [],
                        topRisks: result.topRisks || [],
                        trends: result.trends || {}
                    });
                }
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    // Fetch and aggregate prioritized recommendations from all assets
    useEffect(() => {
        const fetchRecommendations = async () => {
            if (data.assets.length === 0) {
                setRecsLoading(false);
                return;
            }

            try {
                setRecsLoading(true);
                const allRecs = [];

                // Fetch details for each asset (in parallel, limited)
                const assetPromises = data.assets.slice(0, 10).map(async (asset) => {
                    try {
                        const details = await getAsset(asset.assetId);
                        const recs = details?.enrichment?.aiAnalysis?.prioritizedRecommendations || [];
                        return recs.map(rec => ({
                            ...rec,
                            assetId: asset.assetId,
                            assetValue: asset.value,
                            assetRiskScore: asset.riskScore || 0
                        }));
                    } catch {
                        return [];
                    }
                });

                const results = await Promise.all(assetPromises);
                results.forEach(recs => allRecs.push(...recs));

                // Sort by priority (severity), then by asset risk score as tiebreaker
                const priorityOrder = { CRITICAL: 0, HIGH: 1, MEDIUM: 2, LOW: 3, INFO: 4 };
                allRecs.sort((a, b) => {
                    const aPriority = priorityOrder[a.priority] ?? 5;
                    const bPriority = priorityOrder[b.priority] ?? 5;
                    if (aPriority !== bPriority) return aPriority - bPriority;
                    // Lower risk score = more at risk, should come first
                    return (a.assetRiskScore || 100) - (b.assetRiskScore || 100);
                });

                // Limit to top 5
                setRecommendations(allRecs.slice(0, 5));
            } catch (err) {
                console.log('Failed to fetch recommendations:', err);
            } finally {
                setRecsLoading(false);
            }
        };

        if (!loading && data.assets.length > 0) {
            fetchRecommendations();
        }
    }, [loading, data.assets]);

    const { summary, assets, trends } = data;

    // Calculate assets at risk (critical + atRisk levels)
    const assetsAtRisk = useMemo(() => {
        return (summary.riskDistribution?.critical || 0) + (summary.riskDistribution?.atRisk || 0);
    }, [summary.riskDistribution]);

    // Trend chart data
    const trendData = useMemo(() => {
        return (trends.riskScoreHistory || []).slice(-14).map((point, i) => ({
            name: `D${i + 1}`,
            score: point.score || point.riskScore || 0
        }));
    }, [trends.riskScoreHistory]);

    // Toggle section collapse
    const toggleSection = useCallback((section) => {
        setSectionsOpen(prev => ({ ...prev, [section]: !prev[section] }));
    }, []);

    // Handle scan action
    const handleScan = useCallback((asset) => {
        navigate(`/scans?asset=${asset.assetId}&action=quick`);
    }, [navigate]);

    if (loading) {
        return (
            <div className="cm-page">
                <LeftNav />
                <div className="cm-content">
                    <SkeletonDashboard />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="cm-page">
                <LeftNav />
                <div className="cm-content">
                    <div className="cm-error">
                        <span className="error-icon">⚠️</span>
                        <p>{error}</p>
                        <button onClick={() => window.location.reload()}>Retry</button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="cm-page">
            <LeftNav />

            <div className="cm-content">
                {/* Header */}
                <header className="cm-header">
                    <h1>Security Overview</h1>
                    <span className="header-time">
                        Updated {formatRelativeTime(summary.lastScanAt)}
                    </span>
                </header>

                {/* Top Metrics Row */}
                <div className="cm-metrics-row">
                    <MetricCard
                        label="Assets at Risk"
                        value={assetsAtRisk}
                        total={summary.totalAssets || 0}
                        icon="⚠️"
                        color="var(--severity-high)"
                        onClick={() => navigate('/command-center')}
                    />
                    <SeverityCard vulnCounts={summary.totalVulnerabilities} />
                    <ActivityCard
                        lastScan={summary.lastScanAt}
                        nextScan={summary.nextScanAt}
                    />
                </div>

                {/* Trend Section */}
                <section className="cm-section">
                    <CollapsibleHeader
                        title="Security Trend"
                        icon="📈"
                        isOpen={sectionsOpen.trend}
                        onToggle={() => toggleSection('trend')}
                    />
                    {sectionsOpen.trend && trendData.length > 0 && (
                        <div className="section-content">
                            <p className="section-subtitle">14-day security score history</p>
                            <div className="trend-chart">
                                <ResponsiveContainer width="100%" height={160}>
                                    <AreaChart data={trendData}>
                                        <defs>
                                            <linearGradient id="scoreGradientV5" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor={themeColors.accent.primary} stopOpacity={0.3} />
                                                <stop offset="100%" stopColor={themeColors.accent.primary} stopOpacity={0} />
                                            </linearGradient>
                                        </defs>
                                        <XAxis
                                            dataKey="name"
                                            axisLine={false}
                                            tickLine={false}
                                            tick={{ fill: themeColors.text.muted, fontSize: 10 }}
                                        />
                                        <YAxis
                                            domain={[0, 100]}
                                            axisLine={false}
                                            tickLine={false}
                                            tick={{ fill: themeColors.text.muted, fontSize: 10 }}
                                            width={30}
                                        />
                                        <RechartsTooltip
                                            contentStyle={{
                                                background: themeColors.bg.card,
                                                border: `1px solid ${themeColors.border.medium}`,
                                                borderRadius: '6px'
                                            }}
                                        />
                                        <Area
                                            type="monotone"
                                            dataKey="score"
                                            stroke={themeColors.accent.primary}
                                            fill="url(#scoreGradientV5)"
                                            strokeWidth={2}
                                        />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    )}
                </section>

                {/* Prioritized Recommendations Section */}
                <section className="cm-section">
                    <CollapsibleHeader
                        title="Prioritized Recommendations"
                        icon="🎯"
                        isOpen={sectionsOpen.recommendations}
                        onToggle={() => toggleSection('recommendations')}
                        actions={<TeamToggle activeTeam={teamView} onChange={setTeamView} />}
                    />
                    {sectionsOpen.recommendations && (
                        <div className="section-content">
                            {recsLoading ? (
                                <div className="table-loading">Loading recommendations...</div>
                            ) : recommendations.length > 0 ? (
                                <div className="table-wrapper">
                                    <table className="cm-table recommendations-table">
                                        <thead>
                                            <tr>
                                                <th className="col-rank">#</th>
                                                <th className="col-asset">Asset</th>
                                                <th className="col-vuln">Vulnerability</th>
                                                <th className="col-content">
                                                    {teamView === 'red' ? 'Attack Path' : 'Mitigation'}
                                                </th>
                                                <th className="col-effort">Effort</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {recommendations.map((rec, i) => (
                                                <RecommendationRow
                                                    key={`${rec.assetId}-${i}`}
                                                    rec={rec}
                                                    index={i}
                                                    teamView={teamView}
                                                    onClick={() => navigate(`/assets/${rec.assetId}#recommendations`)}
                                                />
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="empty-state">
                                    <span className="empty-icon">✓</span>
                                    <p>No prioritized recommendations available</p>
                                </div>
                            )}
                        </div>
                    )}
                </section>

                {/* Assets Section */}
                <section className="cm-section">
                    <CollapsibleHeader
                        title="Assets"
                        icon="🌐"
                        isOpen={sectionsOpen.assets}
                        onToggle={() => toggleSection('assets')}
                        actions={
                            <button className="section-action" onClick={() => navigate('/assets')}>
                                Manage All →
                            </button>
                        }
                    />
                    {sectionsOpen.assets && (
                        <div className="section-content">
                            {assets.length > 0 ? (
                                <div className="table-wrapper">
                                    <table className="cm-table assets-table">
                                        <thead>
                                            <tr>
                                                <th className="col-status"></th>
                                                <th className="col-name">Asset</th>
                                                <th className="col-score">Score</th>
                                                <th className="col-vulns">Findings</th>
                                                <th className="col-time">Last Scan</th>
                                                <th className="col-actions">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {assets.slice(0, 8).map(asset => (
                                                <AssetRow
                                                    key={asset.assetId}
                                                    asset={asset}
                                                    onClick={() => navigate(`/assets/${asset.assetId}`)}
                                                    onScan={handleScan}
                                                />
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="empty-state">
                                    <span className="empty-icon">📦</span>
                                    <p>No assets monitored yet</p>
                                    <button onClick={() => navigate('/assets')}>
                                        Add Your First Asset
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </section>
            </div>
        </div>
    );
}

export default DashboardCleanModern;
